import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Header,
  ProgressBar,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import {
  Investigation,
  InvestigationRedriveAttempt,
  InvestigationUpdateRequest,
  Stage,
} from "../../lusca-search-api/generated-src";
import {
  InvestigationApiError,
  useInvestigation,
  useUpdateInvestigation,
} from "../../hooks/useInvestigations";
import {
  MONITORING_QUIET_HOURS,
  formatReadyAt,
  monitoringReadyAt,
  monitoringWatchStats,
} from "./MonitoringRedriveWatch";
import { monitoringClear, monitoringClearSummary } from "./readyToResolve";
import { StatusTransitionModal } from "./InvestigationStatus";
import { saveErrorText } from "./versionConflict";

interface RedriveMonitoringSectionProps {
  stage: Stage;
  investigation: Investigation;
  attempt: InvestigationRedriveAttempt | null;
  reconciledAt?: string | null;
  currentUser: string | null;
  onError: (message: string) => void;
  now?: number;
}

/** "0 min", "20 min", "1 h 5 min" */
const formatSpan = (ms: number): string => {
  const minutes = Math.round(ms / 60000);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? `${h} h${m ? ` ${m} min` : ""}` : `${m} min`;
};

/**
 * Redrives tab, MONITORING_REDRIVE only: what is being watched, when Lusca last/next looks,
 * and when the case becomes ready to resolve — with "Resolve now" for an owner who is
 * confident and doesn't want to wait out the quiet period.
 */
export const RedriveMonitoringSection: React.FC<
  RedriveMonitoringSectionProps
> = ({
  stage,
  investigation,
  attempt,
  reconciledAt,
  currentUser,
  onError,
  now: nowProp,
}) => {
  const update = useUpdateInvestigation(stage);
  // Same query as the page (deduplicated): the close dialog names open split-out cases.
  const related = useInvestigation(stage, investigation.id)?.data?.related;
  const [resolving, setResolving] = useState(false);
  // Tick every minute so the quiet-period bar and "in N min" move without a refetch.
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    if (nowProp !== undefined) return undefined;
    const id = window.setInterval(() => setTick(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, [nowProp]);
  const now = nowProp ?? tick;

  const stats = monitoringWatchStats(investigation, attempt, reconciledAt, now);
  const ready = monitoringClear(investigation);
  const readyAt = monitoringReadyAt(attempt);
  const returned = !!(
    investigation.latestRedrive?.hasReturns || attempt?.hasReturns
  );
  // A delete the run fully confirmed resolves at once; one that reported errors waits here for
  // a snapshot to show whether the items are gone. Deleted items cannot come back.
  const deletion = attempt?.kind === "DELETE";
  const isOwner = !!currentUser && investigation.owner === currentUser;
  // Same rule as the Status card: owner, or anyone on an unowned case.
  const canResolve = isOwner || (!!currentUser && !investigation.owner);

  const quietMs = MONITORING_QUIET_HOURS * 3600_000;
  const elapsedMs =
    readyAt === null
      ? 0
      : Math.min(quietMs, Math.max(0, now - (readyAt - quietMs)));
  const percent = ready ? 100 : Math.round((elapsedMs / quietMs) * 100);

  // One headline state so it's obvious what Lusca is doing right now.
  let state: React.ReactNode;
  if (ready) {
    state = <StatusIndicator type="success">Ready to resolve</StatusIndicator>;
  } else if (returned) {
    state = (
      <StatusIndicator type="warning">Redriven items came back</StatusIndicator>
    );
  } else if (readyAt === null) {
    state = (
      <StatusIndicator type="loading">
        Waiting for the redrive to finish
      </StatusIndicator>
    );
  } else if (deletion) {
    state = (
      <StatusIndicator type="loading">
        Waiting for a DLQ snapshot
      </StatusIndicator>
    );
  } else {
    state = (
      <StatusIndicator type="loading">
        Watching the DLQ for returns
      </StatusIndicator>
    );
  }

  let readyValue: string;
  if (ready) {
    readyValue = `Ready now · ${monitoringClearSummary(investigation)}`;
  } else if (returned) {
    readyValue = "Not ready while items are coming back";
  } else if (readyAt === null) {
    readyValue = "Ready 4 h after the redrive completes";
  } else if (deletion) {
    readyValue =
      readyAt > now
        ? `Checked ≈${formatReadyAt(readyAt, now)}`
        : "Checked at the next snapshot";
  } else if (readyAt > now) {
    readyValue = `Ready to resolve ≈${formatReadyAt(readyAt, now)}`;
  } else {
    readyValue = "Ready at the next check with no returns";
  }

  // The elapsed time is already on the bar label, so this line only says how many were redriven.
  const count = attempt?.processedCount ?? attempt?.requestedCount;
  const redrivenCount =
    count === undefined || count === null
      ? stats.redrive
      : `${count.toLocaleString()} item${count === 1 ? "" : "s"} ${
          deletion ? "in the delete" : "redriven"
        }`;
  const checks = [stats.lastChecked, stats.check].filter(Boolean).join(" · ");

  const submit = (request: InvestigationUpdateRequest) =>
    update
      .mutateAsync({
        investigationId: investigation.id,
        request: { ...request, expectedVersion: investigation.version },
      })
      .then(() => setResolving(false))
      .catch((err: InvestigationApiError) => onError(saveErrorText(err)));

  return (
    <Container
      data-testid="redrive-monitoring"
      header={
        <Header
          variant="h3"
          description={
            deletion
              ? "The delete run reported errors, so Lusca checks a DLQ snapshot: if the items are gone the case resolves, otherwise it reopens."
              : "Lusca checks every DLQ snapshot for the redriven items. If any come back, the case reopens."
          }
          info={<span data-testid="monitoring-state">{state}</span>}
          actions={
            <Button
              variant={ready ? "primary" : "normal"}
              ariaLabel={`${
                ready ? "Resolve" : "Resolve now"
              } (opens a dialog)`}
              data-testid="monitoring-resolve"
              disabled={!canResolve}
              disabledReason={
                investigation.owner
                  ? `Only ${investigation.owner} (the owner) can resolve.`
                  : "Sign in to resolve."
              }
              onClick={() => setResolving(true)}
            >
              {ready ? "Resolve…" : "Resolve now…"}
            </Button>
          }
        >
          {deletion ? "Verifying deletion" : "Monitoring"}
        </Header>
      }
    >
      <SpaceBetween size="xs">
        {returned && !ready ? (
          <Box data-testid="monitoring-ready-at">{readyValue}</Box>
        ) : (
          <ProgressBar
            data-testid="monitoring-progress"
            value={percent}
            status={ready ? "success" : "in-progress"}
            resultText={readyValue}
            label={
              deletion
                ? `${formatSpan(
                    elapsedMs
                  )} since the delete · checked ${MONITORING_QUIET_HOURS} h after it`
                : `${formatSpan(
                    elapsedMs
                  )} since the redrive · needs ${MONITORING_QUIET_HOURS} h with no returns`
            }
            additionalInfo={
              ready ? undefined : (
                <span data-testid="monitoring-ready-at">{readyValue}</span>
              )
            }
          />
        )}
        <Box color="text-body-secondary" fontSize="body-s">
          {[redrivenCount, checks].filter(Boolean).join(" · ")}
        </Box>
      </SpaceBetween>
      {resolving && (
        <StatusTransitionModal
          targetStatus="RESOLVED"
          stage={stage}
          investigation={investigation}
          related={related}
          loading={update.isLoading}
          onDismiss={() => setResolving(false)}
          onSubmit={(request) => {
            submit(request).catch(console.error);
          }}
        />
      )}
    </Container>
  );
};

export default RedriveMonitoringSection;
